import { Button, Card, CardContent, Dialog, DialogActions, DialogContent, Stack } from '@mui/material'
import { difference, includes, uniq } from 'lodash'
import PropTypes from 'prop-types'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import i18n from 'simple-react-i18n'
import { NoDataCard } from '../../../../../components/card/NoDataCard'
import { ALIGN, CardTable } from '../../../../../components/datatable/NewTable'
import { SimpleFilterField } from '../../../../../components/datatable/SimpleSelectionTable'
import { DefaultDialogTitle } from '../../../../../components/styled/Dialog'
import { nbPerPageLabel } from '../../../../../referencial/constants/ReferencialConstants'
import useCampaignObjectives from '../../../../../utils/customHook/useCampaignObjectives'
import { hasValue } from '../../../../../utils/NumberUtil'
import { hybridSearch } from '../../../../../utils/SearchUtil'
import { normalizeString } from '../../../../../utils/StringUtil'
import { negIdGenerator } from '../../../../utils/CampaignUtils'
import { StationCampaignContext } from '../../DashboardCampaign'

const PopinCampaignObjectives = ({
    isOpen = false,
    onClose = () => {},
    onValidate = () => {},
}) => {
    const {
        campaign,
        campaignObjectives,
    } = useContext(StationCampaignContext)

    const objectiveList = useCampaignObjectives(campaign.campaignType)

    const [searchValue, setSearchValue] = useState()
    const [selectedCodes, setSelectedCodes] = useState([])

    useEffect(() => {
        if (!isOpen) return
        setSearchValue(undefined)
        setSelectedCodes(campaignObjectives.map(({ objectiveCode }) => objectiveCode))
    }, [isOpen])

    const rows = useMemo(() => hybridSearch(
        objectiveList.map(({ code, name }) => ({
            name,
            isSelected: includes(selectedCodes, code),
            code,
            _normalizedSearchString: [name, `${code}`].map(s => normalizeString(s)),
        })),
        normalizeString(searchValue),
    ), [objectiveList, selectedCodes, searchValue])

    const toggleObjective = ({ isSelected, code }) => setSelectedCodes(prev => (
        isSelected ? prev.filter(objectiveCode => objectiveCode !== code) : [...prev, code]
    ))

    const actions = rows.length > 0 && rows.every(({ isSelected }) => isSelected) ? [{
        onClick: () => setSelectedCodes(prev => difference(prev, rows.map(({ code }) => code))),
        icon: 'check_box',
        tooltip: i18n.unselectAll,
    }] : [{
        onClick: () => setSelectedCodes(prev => uniq([...prev, ...rows.map(({ code }) => code)])),
        icon: 'check_box_outline_blank',
        tooltip: i18n.selectAll,
    }]

    const onConfirm = () => {
        const keptObjectives = campaignObjectives.filter(({ objectiveCode }) => includes(selectedCodes, objectiveCode))
        const addedObjectives = difference(selectedCodes, campaignObjectives.map(({ objectiveCode }) => objectiveCode))
            .map(objectiveCode => ({ id: negIdGenerator.next().value, objectiveCode }))
        const removedIds = campaignObjectives
            .filter(({ objectiveCode }) => !includes(selectedCodes, objectiveCode))
            .map(({ id }) => id)
        onValidate([...keptObjectives, ...addedObjectives], removedIds)
    }

    return (
        <Dialog open={isOpen} fullWidth maxWidth='md'>
            <DefaultDialogTitle title={i18n.objectives} onClose={onClose} />
            <DialogContent>
                {!hasValue(campaign.campaignType) ? (
                    <NoDataCard message={i18n.selectCampaignTypeFirst} />
                ) : (
                    <Stack spacing={1}>
                        <Card>
                            <CardContent>
                                <SimpleFilterField filter={{ searchValue }} setFilter={({ searchValue: value }) => setSearchValue(value)} />
                            </CardContent>
                        </Card>
                        <CardTable
                            title={i18n.objectives}
                            actions={actions}
                            headers={['name']}
                            rows={rows}
                            onClickRow={toggleObjective}
                            lineActions={[{
                                icon: ({ isSelected }) => isSelected ? 'check_box' : 'check_box_outline_blank',
                                align: ALIGN.RIGHT,
                                onClick: toggleObjective,
                            }]}
                            rowsPerPageOptions={nbPerPageLabel}
                            maxHeight='45vh'
                        />
                    </Stack>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onConfirm} variant='contained' color='primary'>
                    {i18n.validate}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

PopinCampaignObjectives.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    onValidate: PropTypes.func,
}

export default PopinCampaignObjectives
